import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../../contexts/LangContext.js';
import { useTheme } from '../../contexts/ThemeContext.js';
import {
  FS_API_SUPPORTED,
  getSaveFolder, getLoadFolder,
  pickSaveFolder, pickLoadFolder,
  clearSaveFolder, clearLoadFolder,
} from '../../services/LayoutService.js';

// ============================================================
// LayoutFolderModal.jsx
// EV Powertrain Simulator — 레이아웃 저장/불러오기 폴더 설정 모달
//
// props:
//   show      : boolean
//   onClose   : () => void
//   onChanged : () => void  — 폴더 변경 후 부모에 알림 (선택적)
// ============================================================

function LayoutFolderModal({ show, onClose, onChanged }) {
  const COLORS = useTheme();
  const lang   = useLang();
  const e      = (ko, en) => lang === 'ko' ? ko : en;
  const mono   = "'JetBrains Mono','Fira Mono',monospace";

  // ── 현재 폴더 이름 상태 ──────────────────────────────────
  const [saveFolder, setSaveFolder] = useState(() => getSaveFolder());
  const [loadFolder, setLoadFolder] = useState(() => getLoadFolder());
  const [busySave,   setBusySave]   = useState(false);
  const [busyLoad,   setBusyLoad]   = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }

  // 모달 열릴 때마다 최신 상태 반영
  useEffect(() => {
    if (show) {
      setSaveFolder(getSaveFolder());
      setLoadFolder(getLoadFolder());
      setMsg(null);
    }
  }, [show]);

  const notify = (ok, text) => {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 3000);
  };

  // ── 저장 폴더 선택 ──────────────────────────────────────
  const handlePickSave = useCallback(async () => {
    setBusySave(true);
    try {
      const res = await pickSaveFolder();
      setSaveFolder(getSaveFolder());
      notify(true, e(`저장 폴더 설정됨: 📁 ${res.name}`, `Save folder set: 📁 ${res.name}`));
      onChanged?.();
    } catch (err) {
      if (err.name !== 'AbortError') notify(false, e(`오류: ${err.message}`, `Error: ${err.message}`));
    } finally { setBusySave(false); }
  }, [onChanged, e]);

  // ── 불러오기 폴더 선택 ──────────────────────────────────
  const handlePickLoad = useCallback(async () => {
    setBusyLoad(true);
    try {
      const res = await pickLoadFolder();
      setLoadFolder(getLoadFolder());
      notify(true, e(`불러오기 폴더 설정됨: 📁 ${res.name}`, `Load folder set: 📁 ${res.name}`));
      onChanged?.();
    } catch (err) {
      if (err.name !== 'AbortError') notify(false, e(`오류: ${err.message}`, `Error: ${err.message}`));
    } finally { setBusyLoad(false); }
  }, [onChanged, e]);

  // ── 저장 폴더 초기화 ────────────────────────────────────
  const handleClearSave = useCallback(async () => {
    await clearSaveFolder();
    setSaveFolder(getSaveFolder());
    notify(true, e('저장 폴더가 초기화되었습니다.', 'Save folder cleared.'));
    onChanged?.();
  }, [onChanged, e]);

  // ── 불러오기 폴더 초기화 ────────────────────────────────
  const handleClearLoad = useCallback(async () => {
    await clearLoadFolder();
    setLoadFolder(getLoadFolder());
    notify(true, e('불러오기 폴더가 초기화되었습니다.', 'Load folder cleared.'));
    onChanged?.();
  }, [onChanged, e]);

  if (!show) return null;

  // ── 공통 스타일 ─────────────────────────────────────────
  const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const modalBox = {
    background: COLORS.panel, borderRadius: 12, width: 'min(560px, 94vw)',
    border: `1px solid ${COLORS.border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    overflow: 'hidden',
  };
  const btnPrimary = (color = '#2563eb') => ({
    padding: '7px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 11, fontWeight: 700, background: color, color: '#fff',
    transition: 'opacity 0.15s', opacity: 1, fontFamily: mono,
  });
  const btnGhost = {
    padding: '6px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
    cursor: 'pointer', fontSize: 11, fontWeight: 600, background: 'transparent',
    color: COLORS.textDim, transition: 'all 0.15s', fontFamily: mono,
  };

  // ── 폴더 행 렌더러 ──────────────────────────────────────
  const FolderRow = ({ label, icon, folder, onPick, onClear, busy, accentColor, disabled }) => (
    <div style={{
      background: COLORS.bg, borderRadius: 8, padding: '14px 16px',
      border: `1px solid ${COLORS.border}`, marginBottom: 12,
    }}>
      {/* 레이블 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{label}</span>
        {disabled && (
          <span style={{
            fontSize: 9, fontWeight: 700, color: '#f59e0b',
            background: '#f59e0b18', border: '1px solid #f59e0b44',
            borderRadius: 4, padding: '1px 6px', fontFamily: mono,
          }}>
            {e('미지원 브라우저', 'Unsupported Browser')}
          </span>
        )}
      </div>

      {/* 현재 경로 표시 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: COLORS.panel, borderRadius: 6, padding: '8px 12px',
        border: `1px solid ${folder.name ? accentColor + '50' : COLORS.border}`,
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 14 }}>{folder.name ? '📁' : '🚫'}</span>
        <span style={{
          flex: 1, fontSize: 12, fontFamily: mono,
          color: folder.name ? COLORS.text : COLORS.textMuted,
          wordBreak: 'break-all',
        }}>
          {folder.name
            ? folder.name
            : e('폴더가 설정되지 않았습니다 (브라우저 기본 폴더 사용)', 'Not set (uses browser default folder)')}
        </span>
        {folder.name && (
          <span style={{
            fontSize: 9, fontWeight: 700, color: accentColor,
            background: accentColor + '18', border: `1px solid ${accentColor}44`,
            borderRadius: 4, padding: '2px 6px', fontFamily: mono, whiteSpace: 'nowrap',
          }}>
            {e('설정됨', 'SET')}
          </span>
        )}
      </div>

      {/* 버튼 행 */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onPick}
          disabled={disabled || busy}
          style={{ ...btnPrimary(disabled ? '#6b7280' : accentColor), opacity: (disabled || busy) ? 0.5 : 1, cursor: (disabled || busy) ? 'not-allowed' : 'pointer' }}>
          {busy ? e('선택 중…', 'Selecting…') : e('📁 폴더 선택', '📁 Browse')}
        </button>
        {folder.name && (
          <button onClick={onClear} disabled={disabled} style={{ ...btnGhost }}>
            {e('✕ 초기화', '✕ Clear')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={e_ => e_.stopPropagation()}>

        {/* 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: `1px solid ${COLORS.border}`,
          background: `linear-gradient(135deg, ${'#7c3aed'}18, ${COLORS.panel})`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📂</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                {e('레이아웃 폴더 설정', 'Layout Folder Settings')}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: mono, marginTop: 2 }}>
                {e('저장 위치 및 불러오기 위치를 개별 지정합니다', 'Configure separate folders for save and load')}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 16, color: COLORS.textMuted, padding: '4px 8px', borderRadius: 4,
          }}>✕</button>
        </div>

        {/* 본문 */}
        <div style={{ padding: '18px 20px' }}>

          {/* API 미지원 경고 배너 */}
          {!FS_API_SUPPORTED && (
            <div style={{
              background: '#f59e0b18', border: '1px solid #f59e0b55', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>
                  {e('폴더 지정 기능 미지원', 'Folder Selection Not Supported')}
                </div>
                <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.6 }}>
                  {e(
                    '현재 브라우저는 File System Access API를 지원하지 않아 폴더를 직접 지정할 수 없습니다. Chrome 또는 Edge를 사용하면 폴더 지정이 가능합니다. 저장 시 브라우저 기본 다운로드 폴더를 사용합니다.',
                    'Your browser does not support the File System Access API. Please use Chrome or Edge to enable folder selection. Files will be saved to the default browser download folder.',
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 저장 폴더 행 */}
          <FolderRow
            label={e('💾  레이아웃 저장 폴더', '💾  Layout Save Folder')}
            icon="💾"
            folder={saveFolder}
            onPick={handlePickSave}
            onClear={handleClearSave}
            busy={busySave}
            accentColor="#16a34a"
            disabled={!FS_API_SUPPORTED}
          />

          {/* 불러오기 폴더 행 */}
          <FolderRow
            label={e('📂  레이아웃 불러오기 폴더', '📂  Layout Load Folder')}
            icon="📂"
            folder={loadFolder}
            onPick={handlePickLoad}
            onClear={handleClearLoad}
            busy={busyLoad}
            accentColor="#2563eb"
            disabled={!FS_API_SUPPORTED}
          />

          {/* 안내 텍스트 */}
          <div style={{
            background: COLORS.bg, borderRadius: 6, padding: '10px 14px',
            border: `1px solid ${COLORS.border}`, fontSize: 10,
            color: COLORS.textMuted, lineHeight: 1.7, fontFamily: mono,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.textDim }}>
              {e('ℹ️  참고', 'ℹ️  Note')}
            </div>
            <div>
              {e(
                '• 저장 폴더 미설정 시 브라우저 기본 다운로드 폴더에 저장됩니다.',
                '• If no save folder is set, files go to the browser\'s default download folder.',
              )}
            </div>
            <div>
              {e(
                '• 불러오기 폴더 설정 시 파일 선택 창이 해당 폴더에서 열립니다.',
                '• If a load folder is set, the file picker will open at that location.',
              )}
            </div>
            <div>
              {e(
                '• 폴더 설정은 브라우저를 닫아도 유지됩니다 (IndexedDB 저장).',
                '• Folder settings persist across browser sessions (stored in IndexedDB).',
              )}
            </div>
            <div>
              {e(
                '• 처음 저장 시 브라우저가 해당 폴더에 대한 쓰기 권한을 요청할 수 있습니다.',
                '• The browser may request write permission the first time you save to a folder.',
              )}
            </div>
          </div>

          {/* 알림 메시지 */}
          {msg && (
            <div style={{
              marginTop: 12, padding: '9px 14px', borderRadius: 6, fontSize: 11,
              fontWeight: 700, fontFamily: mono,
              background: msg.ok ? '#16a34a22' : '#dc262622',
              border:     `1px solid ${msg.ok ? '#22c55e' : '#ef4444'}`,
              color:      msg.ok ? '#4ade80'   : '#f87171',
            }}>
              {msg.text}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', padding: '12px 20px',
          borderTop: `1px solid ${COLORS.border}`, background: COLORS.bg,
        }}>
          <button onClick={onClose} style={btnPrimary('#475569')}>
            {e('닫기', 'Close')}
          </button>
        </div>

      </div>
    </div>
  );
}

export { LayoutFolderModal };
